import { FunctionComponent, useEffect } from 'react';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { TableRow, TableCell, Typography, Box } from '@material-ui/core';
import clsx from 'clsx';
import { Product } from '@spike/product-model';
import ToolbarButton from 'components/UI/Toolbar/ToolbarButton';
import ToolbarItem from 'components/UI/Toolbar/ToolbarItem';
import { faCamera, faPen, faTrash } from '@fortawesome/pro-light-svg-icons';
import { deleteProductThunk, fetchProductsThunk } from '@spike/products-action';
import { useDispatch, useSelector } from 'react-redux';
import { fetchThunk as fetchVendorsThunk } from 'actions/productVendors/ProductVendorsActions';
import { fetchThunk as fetchCategoriesThunk } from 'actions/productCategories/ProductCategoriesActions';
import { ProductsStatus } from '@spike/products-action';
import { RootState } from 'store';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Img } from 'react-image';
import { useApiClientWrapper, useMasterData } from 'hooks';
import ToolbarItemWithConfirmation from '../UI/Toolbar';
import isEmpty from 'lodash/isEmpty';
import { getPriceBySize } from '../Utils';

interface Props {
    product: Product;
    onEdit: (product: Product) => void;
    mobile?: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        row: {
            'backgroundColor': '#FFFFFF',
            'height': 'auto',
            '&:hover': {
                cursor: 'pointer',
                backgroundColor: '#FAFAFA !important'
            }
        },
        cell: {
            paddingLeft: '0px',
            paddingRight: '24px',
            verticalAlign: 'top !important',
            borderBottom: '1px solid #DDDDDD',
            overflow: 'hidden',
            [theme.breakpoints.down('md')]: {
                fontSize: '15px',
                paddingTop: '21px',
                paddingBottom: '21px'
            },
            [theme.breakpoints.up('lg')]: {
                fontSize: '18px',
                paddingTop: '26px',
                paddingBottom: '26px'
            }
        },
        options: {
            display: 'flex',
            justifyContent: 'flex-end',
            flexShrink: 0
        },
        productCell: {
            width: '34%',
            [theme.breakpoints.down(768)]: {
                paddingLeft: 16
            },
            [theme.breakpoints.between(768, 'sm')]: {
                paddingLeft: 27
            },
            [theme.breakpoints.only('md')]: {
                paddingLeft: 43
            },
            [theme.breakpoints.only('lg')]: {
                paddingLeft: 57
            },
            [theme.breakpoints.only('xl')]: {
                paddingLeft: 87
            }
        },
        productInfo: {
            display: 'flex',
            alignItems: 'flex-start',
            gap: '16px',
            minWidth: 0,
            width: '100%'
        },
        cells: {
            width: '13%'
        },
        qtyCell: {
            width: '7%',
            textAlign: 'right',
            paddingRight: '24px'
        },
        retailCell: {
            width: '12%',
            paddingRight: '24px'
        },
        actionsCell: {
            width: '8%',
            textAlign: 'right',
            [theme.breakpoints.down(768)]: {
                paddingRight: 16
            },
            [theme.breakpoints.between(768, 'sm')]: {
                paddingRight: 40
            },
            [theme.breakpoints.only('md')]: {
                paddingRight: 65
            },
            [theme.breakpoints.only('lg')]: {
                paddingRight: 87
            },
            [theme.breakpoints.only('xl')]: {
                paddingRight: 132
            }
        },
        text: {
            fontWeight: 400,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            [theme.breakpoints.down('md')]: {
                fontSize: '13px',
                lineHeight: '22px'
            },
            [theme.breakpoints.up('lg')]: {
                fontSize: '16px',
                lineHeight: '27px'
            }
        },
        nameContainer: {
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            minWidth: 0,
            flex: 1
        },
        textDialog: {
            textAlign: 'center',
            [theme.breakpoints.down('md')]: {
                fontSize: '13px',
                lineHeight: '22px'
            },
            [theme.breakpoints.up('lg')]: {
                fontSize: '16px',
                lineHeight: '27px'
            }
        },
        imageBox: {
            flexShrink: 0,
            overflow: 'hidden',
            [theme.breakpoints.down('md')]: {
                width: '48px',
                height: '45px',
                borderRadius: '8px'
            },
            [theme.breakpoints.up('lg')]: {
                width: '56px',
                height: '52px',
                borderRadius: '10px'
            }
        },
        uploadBox: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            backgroundColor: '#F1F1F1',
            [theme.breakpoints.down('md')]: {
                borderRadius: '8px'
            },
            [theme.breakpoints.up('lg')]: {
                borderRadius: '10px'
            }
        },
        uploadIcon: {
            color: '#5E8677',
            [theme.breakpoints.down('md')]: {
                fontSize: '15px',
                lineHeight: '24px'
            },
            [theme.breakpoints.up('lg')]: {
                fontSize: '18px',
                lineHeight: '30px'
            }
        },
        codeText: {
            color: '#7A7A7A',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            [theme.breakpoints.down('md')]: {
                fontSize: '11px',
                lineHeight: '14px',
                marginTop: '4px'
            },
            [theme.breakpoints.up('lg')]: {
                fontSize: '14px',
                lineHeight: '17px',
                marginTop: '6px'
            }
        },
        titleDialog: {
            fontWeight: 600,
            [theme.breakpoints.down('md')]: {
                fontSize: '19px',
                lineHeight: '29px'
            },
            [theme.breakpoints.up('lg')]: {
                fontSize: '24px',
                lineHeight: '36px'
            }
        },
        capitalize: {
            textTransform: 'capitalize'
        },
        mobileCard: {
            'display': 'flex',
            'flexDirection': 'column',
            'gap': '8px',
            'width': '100%',
            'padding': '16px',
            'background': '#FFFFFF',
            'borderBottom': '1px solid #DDDDDD',
            'cursor': 'pointer',
            '&:hover': {
                backgroundColor: '#FAFAFA'
            }
        },
        mobileName: {
            fontWeight: 600,
            fontSize: '14px',
            lineHeight: '20px',
            color: '#222222',
            wordBreak: 'break-word'
        },
        mobileBarcodeStatus: {
            'display': 'flex',
            'alignItems': 'center',
            'justifyContent': 'space-between',
            'gap': '12px',
            'minWidth': 0,
            '& $codeText': {
                marginTop: 0,
                flex: 1,
                minWidth: 0
            }
        },
        mobileCategory: {
            flexShrink: 0,
            maxWidth: '45%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontSize: '13px',
            lineHeight: '18px',
            fontWeight: 500,
            color: '#222222',
            textAlign: 'right'
        },
        mobileBottom: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            minWidth: 0
        },
        mobileMetrics: {
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '16px',
            minWidth: 0,
            flex: 1
        },
        mobileMetricText: {
            fontSize: '13px',
            lineHeight: '18px',
            color: '#222222'
        }
    })
);

const getProductName = (product: Product): string => {
    if (isEmpty(product.businessProduct?.name)) {
        return product.name || '';
    }
    return product.businessProduct?.name || '';
};

export const ProductRow: FunctionComponent<Props> = props => {
    const classes = useStyles();

    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const masterData = useMasterData();

    const status = useSelector<RootState, ProductsStatus>(state => state.products.status);

    const vendorDefault = props.product && props.product.vendors && props.product.vendors.find(v => v.default === true);

    useEffect(() => {
        if (status === ProductsStatus.DeleteSuccess) {
            dispatch(fetchProductsThunk(apiClientWrapper, '', [], [apiClientWrapper.marketplaceId], [], 1, 10000));
            dispatch(fetchVendorsThunk());
            dispatch(fetchCategoriesThunk());
        }
    }, [status]);

    const handleDeleteProduct = (product: Product) => {
        dispatch(deleteProductThunk(apiClientWrapper, product));
    };

    const toolbar = (
        <Box
            className={classes.options}
            onClick={event => event.stopPropagation()}
        >
            <ToolbarButton>
                <ToolbarItem
                    icon={faPen}
                    onClick={() => props.onEdit(props.product)}
                    text={'View Detail'}
                />
                {props.product.businessProduct?.destroyable && (
                    <ToolbarItemWithConfirmation
                        icon={faTrash}
                        text="Delete"
                        centered
                        onConfirmedClick={() => handleDeleteProduct(props.product)}
                        confirmQuestion={
                            <Typography className={classes.titleDialog}>
                                Are you sure you want to delete this product?
                            </Typography>
                        }
                        subConfirmQuestion={
                            <Typography className={classes.textDialog}>
                                Deleting product will permanently remove it from your database. You can always recreate
                                it in the future.
                            </Typography>
                        }
                        confirmButtonLabel="Delete"
                        cancelButtonLabel="Nevermind"
                    />
                )}
            </ToolbarButton>
        </Box>
    );

    if (props.mobile) {
        return (
            <div
                className={classes.mobileCard}
                onClick={() => props.onEdit(props.product)}
            >
                <div className={classes.mobileName}>{getProductName(props.product)}</div>
                <div className={classes.mobileBarcodeStatus}>
                    <Typography className={classes.codeText}>
                        Barcode: {props.product.businessProduct?.code || '-'}
                    </Typography>
                    <span className={classes.mobileCategory}>{props.product.category?.name || '-'}</span>
                </div>
                <div className={classes.mobileBottom}>
                    <div className={classes.mobileMetrics}>
                        <span className={classes.mobileMetricText}>
                            Qty: {props.product.businessProduct?.stock ?? '-'}
                        </span>
                        <span className={classes.mobileMetricText}>
                            Retail: {getPriceBySize(props.product.businessProduct, masterData)}
                        </span>
                    </div>
                    {toolbar}
                </div>
            </div>
        );
    }

    return (
        <TableRow className={classes.row}>
            <TableCell
                className={clsx(classes.cell, classes.productCell)}
                onClick={() => props.onEdit(props.product)}
            >
                <Box className={classes.productInfo}>
                    <Box className={classes.imageBox}>
                        {props.product.imageUrl ? (
                            <Img
                                src={props.product.imageUrl}
                                className={classes.imageBox}
                            />
                        ) : (
                            <Box className={classes.uploadBox}>
                                <FontAwesomeIcon
                                    icon={faCamera}
                                    className={clsx(classes.uploadIcon)}
                                />
                            </Box>
                        )}
                    </Box>
                    <Box className={classes.nameContainer}>
                        <Typography className={classes.text}>{getProductName(props.product)}</Typography>
                        <Typography className={classes.codeText}>
                            Barcode: {props.product.businessProduct?.code || '-'}
                        </Typography>
                    </Box>
                </Box>
            </TableCell>
            <TableCell
                className={clsx(classes.cell, classes.cells, classes.capitalize)}
                onClick={() => props.onEdit(props.product)}
            >
                <Typography className={classes.text}>
                    {isEmpty(props.product.businessProduct?.brand)
                        ? props.product.brand
                        : props.product.businessProduct?.brand || '-'}
                </Typography>
            </TableCell>
            <TableCell
                className={clsx(classes.cell, classes.cells)}
                onClick={() => props.onEdit(props.product)}
            >
                <Typography className={classes.text}>{props.product.category?.name || '-'}</Typography>
            </TableCell>
            <TableCell
                className={clsx(classes.cell, classes.cells)}
                onClick={() => props.onEdit(props.product)}
            >
                <Typography className={classes.text}>
                    {vendorDefault?.vendor?.name || props.product.vendors.some(vendor => !vendor.vendor?.deleted)
                        ? props.product.vendors.filter(vendor => !vendor.vendor?.deleted)[0].vendor?.name
                        : '-'}
                </Typography>
            </TableCell>
            <TableCell
                className={clsx(classes.cell, classes.qtyCell)}
                onClick={() => props.onEdit(props.product)}
            >
                <Typography className={classes.text}>{props.product.businessProduct?.stock ?? '-'}</Typography>
            </TableCell>
            <TableCell
                className={clsx(classes.cell, classes.retailCell)}
                onClick={() => props.onEdit(props.product)}
            >
                <Typography className={classes.text}>
                    {getPriceBySize(props.product.businessProduct, masterData)}
                </Typography>
            </TableCell>
            <TableCell className={clsx(classes.cell, classes.actionsCell)}>{toolbar}</TableCell>
        </TableRow>
    );
};

export default ProductRow;
